import useAlertService from "@mirage/hooks/useAlertService";
import LocalAttachmentsService from "@mirage/services/LocalAttachmentsService";
import { convertFileToBase64 } from "@mirage/utilities/ConvertToBase64";
import { UploadedFileCollection } from "@shared/mirage/collections/UploadedFileCollection";
import { IFileAttachment, IAttachment } from "@shared/mirage/core/contracts/IAttachment";
import { UploadedFile } from "@shared/mirage/core/contracts/UploadedFile";
import { useCallback, useRef, useState } from "react";

export interface OnBeforeUploadResult {
    abort: boolean;
}

export type OnBeforeUploadReturns = OnBeforeUploadResult | void | Promise<OnBeforeUploadResult> | Promise<void>;

interface AttachmentSaveOptions {
    tray?: string;
    parentEntityName?: string;
    recordId?: number;
    folder?: string;
}

interface UseUploadAttachments {
    toggleForceFetch: () => void;
    service: LocalAttachmentsService;
    onBeforeUpload?: (attachments: IAttachment[]) => OnBeforeUploadReturns;
    attachmentSaveOptions?: AttachmentSaveOptions;
    disableAddNew?: boolean;
}

export interface UploadAttachment {
    uploadAttachmentInput: JSX.Element;
    onDropFileUpload: (files: UploadedFile[]) => Promise<void>;
    uploadingAttIsLoading: boolean;
    inputFileRef: React.RefObject<HTMLInputElement>;
}

const useUploadAttachments = (props: UseUploadAttachments): UploadAttachment => {
    const [uploadingAttIsLoading, setUploadingAttIsLoading] = useState(false);
    const showAlert = useAlertService();
    const { onBeforeUpload, service, attachmentSaveOptions, disableAddNew = false, toggleForceFetch } = props;
    const inputFileRef = useRef<HTMLInputElement>(null);
    const onFilesUploaded = useCallback(
        async (fileAttachments: IFileAttachment[]) => {
            const attachments: IAttachment[] = [];
            const defaultFolder = attachmentSaveOptions?.folder || null;
            for (const file of fileAttachments) {
                const cleanBase64 = file.base64.split(",").pop();
                const a: IAttachment = {
                    body: cleanBase64,
                    parentEntityName: attachmentSaveOptions?.parentEntityName,
                    parentId: attachmentSaveOptions?.recordId,
                    name: file.fileName,
                    tray: attachmentSaveOptions?.tray || "",
                    originalFileName: file.fileName,
                    folder: defaultFolder,
                    access: "Internal",
                } as IAttachment;
                attachments.push(a);
            }
            if (onBeforeUpload) {
                const result = await onBeforeUpload(attachments);
                if (result?.abort) {
                    return;
                }
            }
            const result = await service.insertAttachments(attachments, true);
            if (result.errorMsg) {
                showAlert(result.errorMsg, "error");
            } else if (result.errors.length > 0) {
                const error = result.errors.map((e) => e.errorMsg).join(", ");
                showAlert(error, "error");
            } else {
                toggleForceFetch();
                // setForceFetch((prev) => !prev);
                showAlert("Attachments uploaded successfully", "success");
            }
        },
        [
            attachmentSaveOptions?.folder,
            attachmentSaveOptions?.parentEntityName,
            attachmentSaveOptions?.recordId,
            attachmentSaveOptions?.tray,
            onBeforeUpload,
            service,
            showAlert,
            toggleForceFetch,
        ]
    );
    const uploadAttachments = useCallback(
        async (files: FileList | null) => {
            const fileAttachments: IFileAttachment[] = [];
            if (!files || files.length === 0) {
                return;
            }
            for (const file of files) {
                let fileInBase64 = await convertFileToBase64(file);
                if (fileInBase64) {
                    fileInBase64 = String(fileInBase64);
                    fileAttachments.push({ base64: fileInBase64, fileName: file.name });
                }
            }
            await onFilesUploaded(fileAttachments);
            setUploadingAttIsLoading(false);
        },
        [onFilesUploaded]
    );

    const onDropFileUpload = useCallback(
        async (files: UploadedFile[]) => {
            if (disableAddNew) {
                return;
            }
            setUploadingAttIsLoading(true);
            const uploadedFileCollection = new UploadedFileCollection(files);
            const fileAttachments = uploadedFileCollection.convertToFileAttachments();
            await onFilesUploaded(fileAttachments);
            setUploadingAttIsLoading(false);
        },
        [disableAddNew, onFilesUploaded]
    );

    const uploadAttachmentsClick = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            setUploadingAttIsLoading(true);
            const files = e.target.files;
            await uploadAttachments(files);
            e.target.value = "";
        },
        [uploadAttachments]
    );

    const input = <input ref={inputFileRef} hidden accept="*/*" multiple type="file" onChange={uploadAttachmentsClick} />;

    return { uploadAttachmentInput: input, inputFileRef, onDropFileUpload, uploadingAttIsLoading };
};

export default useUploadAttachments;
